import * as React from 'react'
import { useState, useRef, useEffect } from 'react'
import {
  File,
  FileUp,
  AlertCircle,
  Loader2,
  Upload,
  Download,
} from 'lucide-react'
import { Button } from './button'
import { cn } from '../../lib/utils'

interface UploadCardProps {
  title?: string
  accept: string
  maxSizeMB: number
  buttonLabel: string
  helperText: string | React.ReactNode
  templateLink?: { href: string; label: string }
  ariaLabel: string
  emptyStateIcon?: React.ReactNode
  onFileSelect: (file: File | null) => void
  currentFile?: File | null
  errorText?: string
  isUploading?: boolean
  variant?: 'default' | 'link-in-dropzone'
  onRemove?: () => void
}

export function UploadCard({
  title,
  accept,
  maxSizeMB,
  buttonLabel,
  helperText,
  templateLink,
  ariaLabel,
  emptyStateIcon,
  onFileSelect,
  currentFile,
  errorText,
  isUploading = false,
  variant = 'default',
  onRemove,
}: UploadCardProps) {
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const errorBarRef = useRef<HTMLDivElement>(null)
  const [resolvedSize, setResolvedSize] = useState<number | null>(null)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    if (!isUploading) {
      setIsDragOver(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    if (isUploading) return
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) onFileSelect(files[0])
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length > 0) onFileSelect(files[0])
  }

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onRemove) onRemove()
    else onFileSelect(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleReplace = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (fileInputRef.current) fileInputRef.current.value = ''
    fileInputRef.current?.click()
  }

  const handleDropzoneClick = () => {
    if (!isUploading) fileInputRef.current?.click()
  }

  const handleDropzoneKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && !isUploading) {
      e.preventDefault()
      fileInputRef.current?.click()
    }
  }

  const getFileSizeLabel = (file?: File | null) => {
    if (!file) return ''
    const sizeCandidate =
      resolvedSize ?? (file as any).serverSize ?? file.size ?? Number.NaN
    if (!Number.isFinite(sizeCandidate) || sizeCandidate <= 0) return ''
    if (sizeCandidate >= 1024 * 1024)
      return `${(sizeCandidate / 1024 / 1024).toFixed(2)} MB`
    if (sizeCandidate >= 1024) return `${(sizeCandidate / 1024).toFixed(2)} KB`
    return `${sizeCandidate} B`
  }

  useEffect(() => {
    setResolvedSize(null)
    if (!currentFile || errorText) return

    const directSize = (currentFile as any).serverSize ?? currentFile.size
    if (Number.isFinite(directSize) && directSize > 0) {
      setResolvedSize(directSize)
      return
    }

    const serverUrl = (currentFile as any).serverUrl
    if (typeof serverUrl !== 'string' || !serverUrl) return

    let cancelled = false
    const resolveFromDataUri = () => {
      if (!serverUrl.startsWith('data:')) return false
      try {
        const base64Index = serverUrl.indexOf('base64,')
        if (base64Index !== -1) {
          const base64Data = serverUrl.slice(base64Index + 7)
          const bytes = Math.floor((base64Data.length * 3) / 4)
          if (bytes > 0) {
            setResolvedSize(bytes)
            return true
          }
        } else {
          const commaIndex = serverUrl.indexOf(',')
          const raw = commaIndex !== -1 ? serverUrl.slice(commaIndex + 1) : ''
          const decoded = decodeURIComponent(raw)
          const blob = new Blob([decoded])
          if (blob.size > 0) {
            setResolvedSize(blob.size)
            return true
          }
        }
      } catch {
        // ignore decoding errors
      }
      return false
    }

    if (resolveFromDataUri()) return

    const fetchSize = async () => {
      const optionSets = serverUrl.startsWith('http')
        ? [
            { credentials: 'include' as const },
            { credentials: 'omit' as const },
          ]
        : [{}]

      const tryHead = async (options: RequestInit) => {
        try {
          const response = await fetch(serverUrl, {
            method: 'HEAD',
            ...options,
          })
          if (!response.ok) return null
          const length = response.headers.get('content-length')
          if (!length) return null
          const sizeNumber = Number(length)
          return Number.isFinite(sizeNumber) && sizeNumber > 0
            ? sizeNumber
            : null
        } catch {
          return null
        }
      }

      const tryRange = async (options: RequestInit) => {
        try {
          const response = await fetch(serverUrl, {
            method: 'GET',
            headers: { Range: 'bytes=0-0' },
            ...options,
          })
          if (!response.ok) return null
          const rangeHeader = response.headers.get('content-range')
          if (!rangeHeader) return null
          const match = /\/(\d+)$/i.exec(rangeHeader)
          if (!match) return null
          const sizeNumber = Number(match[1])
          return Number.isFinite(sizeNumber) && sizeNumber > 0
            ? sizeNumber
            : null
        } catch {
          return null
        }
      }

      const tryFetch = async (options: RequestInit) => {
        try {
          const response = await fetch(serverUrl, {
            method: 'GET',
            ...options,
          })
          if (!response.ok) return null
          const length = response.headers.get('content-length')
          if (length) {
            const sizeNumber = Number(length)
            if (Number.isFinite(sizeNumber) && sizeNumber > 0) return sizeNumber
          }
          const blob = await response.blob()
          return blob.size > 0 ? blob.size : null
        } catch {
          return null
        }
      }

      const strategies = [tryHead, tryRange, tryFetch]
      for (const options of optionSets) {
        for (const strategy of strategies) {
          if (cancelled) return
          const size = await strategy(options)
          if (size && !cancelled) {
            setResolvedSize(size)
            return
          }
        }
      }
    }

    fetchSize()

    return () => {
      cancelled = true
    }
  }, [currentFile, errorText])

  const TemplateSpan = () => (
    <span className="text-xs text-neutral-400">
      {templateLink ? (
        <>
          {helperText} • Use the{' '}
          <a
            href={templateLink.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-primary hover:underline inline-flex items-center gap-1"
            download
            onClick={(e) => e.stopPropagation()}
          >
            {templateLink.label}
            <Download className="w-3 h-3" />
          </a>
        </>
      ) : (
        <>{helperText}</>
      )}
    </span>
  )

  if (variant === 'link-in-dropzone') {
    return (
      <div className="space-y-3">
        {title && (
          <label className="text-sm font-medium text-neutral-700">
            {title}
          </label>
        )}
        <div
          role="button"
          tabIndex={0}
          className={cn(
            'border-dashed border rounded-lg py-4 px-4 w-full transition-colors cursor-pointer',
            isDragOver && 'border-neutral-300 bg-neutral-50',
            errorText && 'border-status-error',
            !isDragOver &&
              !errorText &&
              'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50',
            isUploading && 'opacity-50 pointer-events-none',
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleDropzoneClick}
          onKeyDown={handleDropzoneKeyDown}
          aria-label={ariaLabel}
        >
          {isUploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-5 h-5 text-neutral-400 animate-spin" />
              <p className="text-sm text-neutral-600">Uploading…</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              {emptyStateIcon || (
                <Upload className="w-5 h-5 text-neutral-400" />
              )}
              <div className="text-center">
                <button
                  type="button"
                  className="text-brand-primary hover:underline text-sm font-medium"
                  onClick={(e) => {
                    e.stopPropagation()
                    fileInputRef.current?.click()
                  }}
                >
                  {buttonLabel}
                </button>
                <span className="text-neutral-400 text-sm ml-1">
                  or drag and drop
                </span>
              </div>
              <div className="text-xs text-neutral-400 text-center">
                <div className="flex items-center justify-center gap-1 flex-wrap">
                  <TemplateSpan />
                </div>
              </div>
            </div>
          )}
        </div>

        {currentFile && !errorText && (
          <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-md">
            <div className="flex items-center gap-2">
              <File className="w-4 h-4 text-neutral-600" />
              <span className="text-sm font-medium">{currentFile.name}</span>
              {getFileSizeLabel(currentFile) && (
                <span className="text-xs text-neutral-500">
                  ({getFileSizeLabel(currentFile)})
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleReplace}
                className="text-brand-primary border-brand-primary hover:text-brand-hover hover:border-brand-hover bg-transparent"
              >
                Replace
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRemoveFile}
                className="text-neutral-500 hover:text-brand-hover"
              >
                Remove
              </Button>
            </div>
          </div>
        )}

        {errorText && (
          <div
            ref={errorBarRef}
            role="alert"
            aria-live="polite"
            tabIndex={-1}
            className="flex items-center gap-2 focus:outline-none"
          >
            <AlertCircle className="w-4 h-4 text-status-error" />
            <p className="text-status-error text-xs">{errorText}</p>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileSelect}
          className="hidden"
          aria-label={ariaLabel}
        />
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {title && (
        <label className="text-sm font-medium text-neutral-700">{title}</label>
      )}
      <div
        role="button"
        tabIndex={0}
        className={cn(
          'border-dashed rounded-md p-4 text-center transition-colors border px-6 py-8 cursor-pointer',
          isDragOver && 'border-brand-primary bg-brand-subtleBG',
          errorText && 'border-status-error border-2',
          !isDragOver &&
            !errorText &&
            'border-neutral-200 hover:border-neutral-600',
          isUploading && 'opacity-50 pointer-events-none',
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleDropzoneClick}
        onKeyDown={handleDropzoneKeyDown}
        aria-label={ariaLabel}
      >
        {isUploading ? (
          <div className="space-y-2">
            <Loader2 className="w-8 h-8 mx-auto mb-2 text-neutral-400 animate-spin" />
            <p className="text-sm text-neutral-600">Uploading…</p>
          </div>
        ) : currentFile && !errorText ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <File className="w-5 h-5 text-neutral-600" />
              <span className="text-sm font-medium">{currentFile.name}</span>
              {getFileSizeLabel(currentFile) && (
                <span className="text-xs text-neutral-500">
                  ({getFileSizeLabel(currentFile)})
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleReplace}
                className="text-brand-primary border-brand-primary hover:text-brand-hover hover:border-brand-hover bg-transparent"
              >
                Replace
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRemoveFile}
                className="text-neutral-500 hover:text-brand-hover"
              >
                Remove
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {emptyStateIcon || (
              <FileUp className="mx-auto mb-2 text-neutral-400 h-6 w-6" />
            )}
            <div className="text-center">
              <button
                type="button"
                className="text-brand-primary hover:underline text-sm font-medium"
                onClick={(e) => {
                  e.stopPropagation()
                  fileInputRef.current?.click()
                }}
              >
                {buttonLabel}
              </button>
              <span className="text-sm ml-1 text-neutral-600">
                or drag and drop
              </span>
            </div>
            <div className="text-xs text-neutral-400 text-center">
              <div className="flex items-center justify-center gap-1 flex-wrap">
                <TemplateSpan />
              </div>
            </div>
          </div>
        )}
      </div>

      {errorText && (
        <div
          ref={errorBarRef}
          role="alert"
          aria-live="polite"
          tabIndex={-1}
          className="flex items-center gap-2 focus:outline-none"
        >
          <AlertCircle className="w-4 h-4 text-status-error" />
          <p className="text-status-error text-xs">{errorText}</p>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        className="hidden"
        aria-label={ariaLabel}
      />
    </div>
  )
}
